//#region IMPORTS
import {
  fetchInstoreTemplateAsync,
  instoreTemplatesSelectors,
} from "../instoreTemplates/instoreTemplatesSlice";
import {
  languageSelectors,
  fetchLanguagesAsync,
} from "../languages/languageSlice";
import { useAppDispatch, useAppSelector } from "../../store/configureStore";
import { createInstoreOrderAsync } from "../instoreOrders/instoreOrdersSlice";
import { InstoreOrderRequest } from "../../models/instoreOrder";
import { useEffect, useState } from "react";
import { clearInstoreBasket } from "./instoreBasketSlice";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import { format } from "date-fns";
import InstoreBasketConfirmItem from "./InstoreBasketConfirmItem";
import ConditionalParagraph from "../../components/genericTextLine/ConditionalParagraph";
import LoadingSpinner from "../../components/loading/LoadingSpinner";
import useHQmanager from "../../hooks/useHQmanager";
import usePickerManager from "../../hooks/usePickerManager";
import PageHeader from "../../components/genericPageHeader/PageHeader";
import EmptyList from "../../components/errors/EmptyList";
//#endregion

export default function InstoreBasketConfirm() {
  //#region SETUP
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useAppDispatch();

  const { user } = useAppSelector((state) => state.account);
  const isHq = useHQmanager(user);
  const isPicker = usePickerManager(user);
  const { items, template_id, store: impersonatedStore } = useAppSelector(
    (state) => state.instoreBasket
  );
  const template = useAppSelector((state) =>
    template_id != null
      ? instoreTemplatesSelectors.selectById(state, template_id)
      : undefined
  );
  const { lastLoadedTemplateId } = useAppSelector(
    (state) => state.instoreTemplates
  );

  const [submitting, setSubmitting] = useState(false);
  const [manualLanguage, setManualLanguage] = useState("");

  // HQ orders on behalf of an impersonated store
  const orderStore = impersonatedStore ?? user?.store ?? null;

  const languages = useAppSelector(languageSelectors.selectAll);
  const { languages_loaded } = useAppSelector(
    (state) => state.languagesManagement
  );

  const storeLanguage = orderStore?.language ?? "";
  const needsManualLanguage = !!orderStore && !storeLanguage;
  const effectiveLanguage = storeLanguage || manualLanguage;

  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);
  // Approval threshold is based on the number of order lines (distinct items),
  // not the total quantity.
  const lineCount = items.length;
  //#endregion

  //#region LOGIC
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (template_id && template_id !== lastLoadedTemplateId) {
      dispatch(fetchInstoreTemplateAsync(template_id));
    }
  }, [template_id, lastLoadedTemplateId, dispatch]);

  useEffect(() => {
    if (needsManualLanguage && !languages_loaded) {
      dispatch(fetchLanguagesAsync());
    }
  }, [needsManualLanguage, languages_loaded, dispatch]);

  const handleConfirm = async () => {
    if (!orderStore || !template) return;
    setSubmitting(true);

    const store = orderStore;
    const orderCode = `${template.order_type_instore_short_name}__${
      store.external_store_id
    }__${format(new Date(), "yyyyMMddHHmm")}`;

    // HQ and Picker skip approval entirely. For customers, 21+ order lines
    // (distinct items) require approval → Pending.
    const orderStatus =
      isHq || isPicker ? "Ordered" : lineCount > 20 ? "Pending" : "Ordered";

    const request: InstoreOrderRequest = {
      order_status: orderStatus,
      order_code: orderCode,
      order_type_instore_id: template.order_type_instore_id,
      order_date: format(new Date(), "yyyy-MM-dd HH:mm"),
      instore_type_id: template.instore_type_id,
      store_format_id: store.store_format_id,
      template_id: template.id,
      store_id: store.id,
      external_store_id: store.external_store_id,
      store_name: store.store_name,
      store_opening_date: store.opening_date,
      store_language: effectiveLanguage,
      store_contact_first_name: store.contact_first_name,
      store_contact_last_name: store.contact_last_name,
      store_contact_phone: store.contact_phone,
      store_contact_email: store.contact_email,
      store_addres_street_1: store.address_1,
      store_addres_street_2: store.address_2,
      store_addres_street_3: store.address_3,
      store_addres_zip_code: store.postal_code,
      store_addres_city: store.city,
      store_addres_country: store.country_name,
      delivery_addres_street_1: store.delivery_address_address_1,
      delivery_addres_street_2: store.delivery_address_address_2,
      delivery_addres_street_3: store.delivery_address_address_3,
      delivery_addres_zip_code: store.delivery_address_postal_code,
      delivery_addres_city: store.delivery_address_city,
      delivery_addres_country: store.delivery_address_country_name,
      store_digital_frame: store.digital_frame_name ?? undefined,
      instore_order_lines: items.map((item, idx) => ({
        theme_id: item.theme_id,
        theme_name: item.theme,
        store_format_id: item.store_format_id,
        store_format_name: item.store_format,
        article_name: item.article_name,
        amount: item.quantity,
        custom_fields: (item.custom_fields ?? []).map((cf) => ({
          fieldName: "",
          fieldValue: cf,
        })),
        index: idx,
      })),
    };

    try {
      await dispatch(createInstoreOrderAsync({ values: request })).unwrap();
      dispatch(clearInstoreBasket());
      history.push("/");
    } catch (error) {
      toast.error(`${error}`);
      setSubmitting(false);
    }
  };
  //#endregion

  //#region FAILSAFE
  if (items.length === 0)
    return <EmptyList message={t("rituals.yourBasketIsEmpty")} />;
  if (!orderStore)
    return <EmptyList message="NO STORE LINKED TO THIS ORDER!" />;
  if (submitting) return <LoadingSpinner />;
  //#endregion

  //#region RENDER
  return (
    <div className="pb-4">
      <PageHeader title={t("rituals.checkout")} />

      <main className="lg:grid lg:grid-cols-8 lg:gap-x-8 text-lg">
        <div className="lg:col-span-5">
          {items.map((item) => (
            <InstoreBasketConfirmItem key={item.template_row_id} item={item} />
          ))}
        </div>

        <section className="lg:col-span-3 mt-4 lg:mt-0 p-4 lg:p-6 bg-[#f2f2f2] flex flex-col justify-between text-base">
          <div>
            <h2 className="mt-4 mb-2 font-semibold">{t("rituals.shop")}</h2>
            <p>
              {orderStore.store_name} / {orderStore.external_store_id}
            </p>
            <p>
              {t("rituals.items")}: {items.length}
            </p>
            <p>
              {t("rituals.totalQuantity")}: {itemCount}
            </p>

            <h2 className="mt-4 mb-2 font-semibold">
              {t("rituals.deliveryAddress")}
            </h2>
            <div>
              <ConditionalParagraph
                value={orderStore.delivery_address_address_1}
              />
              <ConditionalParagraph
                value={orderStore.delivery_address_address_2}
              />
              <ConditionalParagraph
                value={orderStore.delivery_address_address_3}
              />
              <ConditionalParagraph
                inline
                value={orderStore.delivery_address_postal_code}
              />
              <ConditionalParagraph
                inline
                margin="0 0 0 0.5rem"
                value={orderStore.delivery_address_city}
              />
              <ConditionalParagraph
                value={orderStore.delivery_address_country_name}
              />
            </div>

            {needsManualLanguage && (
              <div className="mt-4">
                <h2 className="mb-2 font-semibold">{t("rituals.language")}</h2>
                <select
                  value={manualLanguage}
                  onChange={(e) => setManualLanguage(e.target.value)}
                  className="w-full border-2 border-black p-2 bg-white text-black"
                >
                  <option value="">
                    Pick a language…
                  </option>
                  {languages_loaded &&
                    languages
                      .slice()
                      .sort((a, b) => a.code.localeCompare(b.code))
                      .map((language) => (
                        <option key={language.id} value={language.code}>
                          {language.code}
                        </option>
                      ))}
                </select>
              </div>
            )}
          </div>

          <button
            onClick={handleConfirm}
            disabled={!template || !effectiveLanguage}
            className="w-full mt-4 py-4 px-4 border border-black bg-black text-white font-semibold disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {t("rituals.confirmOrder")}
          </button>
        </section>
      </main>
    </div>
  );
  //#endregion
}
